import HomeAction from 'home/actions/HomeAction'
import { orderBy } from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ReferencialAction from 'referencial/action/ReferencialAction'
import ManagementUnitAction from 'referencial/components/managementUnit/actions/ManagementUnitAction'
import i18n from 'simple-react-i18n'
import useTitle from 'utils/customHook/useTitle'
import { componentHasHabilitations } from 'utils/HabilitationUtil'
import { H_REFERENCIAL_DASHBOARD, H_REFERENCIAL_LEXICONS, H_REFERENCIAL_MAT_TYPES, H_REFERENCIAL_SANDRE_CODES, H_REFERENCIAL_TAXON, H_REFERENCIAL_UNIT_MANAGMENT } from '../../../../account/constants/AccessRulesConstants'
import { REFERENCIAL } from '../../../../home/constants/RouteConstants'
import HabilitationRequired from '../../../../utils/components/HabilitationRequired'
import DashboardAction from '../actions/DashboardAction'
import { Grid } from '@mui/material'
import ReferencialCard from './ReferencialCard'
import TaxonAction from 'referencial/components/taxon/actions/TaxonAction'

const ReferencialDashboardApp = () => {
    const {
        sandreCodes,
        materielTypes,
        managementUnits,
        referencialSandreCodes,
        city,
        contact,
        network,
        fraction,
        contributor,
        watermass,
        method,
        parameter,
        support,
        unit,
        hydrogeologicalEntity,
        watershed,
        taxon,
        aquifer,
        culture,
        activity,
        country,
        rpg,
        sector,
        ecobioTraits,
    } = useSelector(store => ({
        referencialSandreCodes: store.ReferencialReducer.referencialSandreCodes,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        materielTypes: store.ReferencialReducer.materielTypes,
        managementUnits: store.ManagementUnitReducer.managementUnits,
        city: store.DashboardReducer.city,
        contact: store.DashboardReducer.contact,
        network: store.DashboardReducer.network,
        fraction: store.DashboardReducer.fraction,
        contributor: store.DashboardReducer.contributor,
        watermass: store.DashboardReducer.watermass,
        method: store.DashboardReducer.method,
        parameter: store.DashboardReducer.parameter,
        support: store.DashboardReducer.support,
        unit: store.DashboardReducer.unit,
        hydrogeologicalEntity: store.DashboardReducer.hydrogeologicalEntity,
        watershed: store.DashboardReducer.watershed,
        taxon: store.DashboardReducer.taxon,
        aquifer: store.DashboardReducer.aquifer,
        culture: store.DashboardReducer.culture,
        activity: store.DashboardReducer.activity,
        country: store.DashboardReducer.country,
        rpg: store.DashboardReducer.rpg,
        sector: store.DashboardReducer.sector,
        ecobioTraits: store.TaxonReducer.ecobioTraits,
    }), shallowEqual)

    const referencials = useMemo(() => [
        city,
        contact,
        network,
        fraction,
        contributor,
        watermass,
        method,
        parameter,
        support,
        unit,
        hydrogeologicalEntity,
        watershed,
        taxon,
        aquifer,
        culture,
        activity,
        country,
        rpg,
        sector,
    ], [activity, aquifer, city, contact, contributor, country, culture, fraction, hydrogeologicalEntity, method, network, parameter, support, taxon, unit, watermass, watershed, rpg, sector])

    const dispatch = useDispatch()

    useEffect(() => {
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }

        dispatch(DashboardAction.fetchEveryCount())
        dispatch(ReferencialAction.fetchReferencialSandreCodes())
        dispatch(ReferencialAction.fetchMaterielTypes())
        dispatch(ManagementUnitAction.fetchManagementUnits())
        dispatch(TaxonAction.fetchEcobioTraits())

        dispatch(HomeAction.setHelpLink('', ''))
    }, [])

    useTitle(() => [{
        title: i18n.referencials,
        href: REFERENCIAL,
    }, {
        title: i18n.dashboard,
        href: REFERENCIAL,
    }], [])

    const refNumber = useMemo(() => sandreCodes.filter(sandreCode => referencialSandreCodes.some(rsc => rsc.code && rsc.field === sandreCode.field)).length, [referencialSandreCodes, sandreCodes])

    const ref = useMemo(() => ({
        ...referencials,
        sandreCodes: {
            id: 'SANDRE_CODES',
            icon: 'list',
            url: 'sandreCodes',
            name: i18n.sandreNomenclature,
            date: '',
            number: refNumber,
            habilitation: H_REFERENCIAL_SANDRE_CODES,
        },
        lexicons: {
            id: 'LEXICONS',
            icon: 'list',
            url: 'lexicons',
            name: i18n.lexicons,
            date: '',
            number: sandreCodes.length - refNumber,
            habilitation: H_REFERENCIAL_LEXICONS,
        },
        matTypes: {
            id: 'MAT_TYPES',
            icon: 'router',
            url: 'materielTypes',
            name: i18n.materielTypes,
            date: '',
            number: materielTypes.length,
            habilitation: H_REFERENCIAL_MAT_TYPES,
        },
        managementUnits: {
            id: 'MANAGEMENT_UNITS',
            icon: 'device_hub',
            url: 'managementUnit',
            name: i18n.managementUnits,
            date: '',
            number: managementUnits.length,
            habilitation: H_REFERENCIAL_UNIT_MANAGMENT,
        },
        ecoFriendlyFeatures: {
            id: 'ECO_FRIENDLY_FEATURES',
            icon: 'biotech',
            url: 'ecoFriendlyFeatures',
            name: i18n.ecoFriendlyFeatures,
            date: '',
            number: ecobioTraits.length,
            habilitation: H_REFERENCIAL_TAXON,
        },
    }), [managementUnits.length, materielTypes.length, refNumber, referencials, sandreCodes.length, ecobioTraits.length])

    const referencialFiltered = useMemo(() => orderBy(ref, 'name', 'asc').filter(r => componentHasHabilitations(r.habilitation)), [ref])

    return (
        <HabilitationRequired habilitation={H_REFERENCIAL_DASHBOARD}>
            <Grid container spacing={1}>
                {referencialFiltered.map(referencial => (
                    <ReferencialCard
                        key={referencial.id}
                        referencials={referencials}
                        referencialSandreCodes={referencialSandreCodes}
                        url={referencial.url}
                        name={referencial.name}
                        icon={referencial.icon}
                        id={referencial.id}
                        number={referencial.number}
                    />
                ))}
            </Grid>
        </HabilitationRequired>
    )
}

export default ReferencialDashboardApp
